/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import DatahubAPI from '../../api/DatahubAPI';
import CircularProgress from '@mui/material/CircularProgress';
import TradeForm from '../../components/TradeProtection/TradeForm';
import HSCodesHitBreakdown from '../../components/TradeProtection/HSCodesHitBreakdown';
import EntityResolution from 'components/Common/EntityResolution';
import { Typography } from '@mui/material';
import { Redirect } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import { useSnackbar } from 'notistack';
import { exportToCSV } from 'components/Common/CSVManage';
import {
  ModuleContentTooltip,
  NextButton,
  StyledInput,
} from '../../components/Common/MUIStyledComponents';
import Modal from 'react-modal';
import { Close } from '@mui/icons-material';
import ModuleHeaderLayout from 'components/Common/Layouts/ModuleHeaderLayout';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';

const TradeProtectionReact: React.FC = () => {
  // PAYMENTS STATE AND FUNCTIONS
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const requiredTiers = ['pro', 'business', 'enterprise'];
  const [userHasTrade, setUserHasTrade] = useState(
    cur_user?.bite_super_admin === true ||
      requiredTiers.includes(cur_user.license),
  );
  const isInteractionDisabled = !userHasTrade;
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  useEffect(() => {
    setUserHasTrade(
      cur_user?.bite_super_admin === true ||
        requiredTiers.includes(cur_user.license),
    );
  }, [cur_user.license]);

  // PAGE STATE AND FUNCTIONS
  const [tableData, setTableData] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [aggData, setAggData] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: [] });
  const [tradePrimaryOpts, setTradePrimaryOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [codeOpts, setCodeOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [chapterOpts, setChapterOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [listOpts, setListOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [sourceCountryOpts, setSourceCountryOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [agencyOpts, setAgencyOpts] = useState<{
    isLoading: boolean;
    data: any;
  }>({ isLoading: true, data: {} });
  const [pageNum, setPageNum] = useState<number>(0);
  const [selectedIndex, setSelectedIndex] = useState<string>('HS Codes');
  const [entityToggle, setEntityToggle] = useState(false);
  const [fileNameModalOpen, setFileNameModalOpen] = useState(false);
  const [fileName, setFileName] = useState('');

  // file modal
  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };

  const handleFileNameClose = () => {
    setFileNameModalOpen(false);
    setFileName('');
  };

  const toggleModal = () => {
    if (isInteractionDisabled) {
      return;
    }
    setFileNameModalOpen(true);
  };

  const handleCSV = () => {
    if (isInteractionDisabled) {
      return;
    }
    if (tableData.data.hits.hits.length >= 1) {
      switch (selectedIndex) {
        case 'HS Codes':
          exportToCSV(
            fileName,
            [...tableData.data.hits.hits],
            ['HS Code', 'Section', 'Chapter', 'Product Name'],
            'hsCodes',
          );
          break;
        case 'Regimes':
          exportToCSV(
            fileName,
            [...tableData.data.hits.hits],
            [
              'HS Code',
              'Product Name',
              'Agency',
              'Source Country',
              'Risk Country',
            ],
            'regimes',
          );
          break;
        default:
          enqueueSnackbar('Error Exporting Data', { variant: 'error' });
      }
    } else {
      enqueueSnackbar('No Result Data to Export', { variant: 'error' });
    }
  };

  // FORM VALUES
  const [formValues, setFormValues] = useState({
    searchValue: '',
    hsCodeSection: [],
    hsCodeChapter: [],
    hsCode: [],
    sourceCountry: [],
    agency: [],
    state: [],
    listName: [],
    fuzziness: 0,
  });

  const handleIndexChange = (index: string) => {
    if (isInteractionDisabled) {
      return;
    }
    switch (index) {
      case 'HS Codes':
        setSelectedIndex('HS Codes');
        break;
      case 'Regimes':
        setSelectedIndex('Regimes');
        break;
      default:
        setSelectedIndex('HS Codes');
    }
  };

  const getInitialResults = async () => {
    if (isInteractionDisabled) {
      setTableData({ isLoading: false, data: { hits: { hits: [], total: { value: 0 } } } });
      setAggData({ isLoading: false, data: {} });
      return;
    }
    const res = await DatahubAPI.trade({
      indexName: selectedIndex,
      agg: true,
      pageNum: pageNum,
    });
    if (res?.success) {
      setAggData({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
      setTableData({ isLoading: false, data: res?.result?.result?.body });
      setTradePrimaryOpts({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
    }
  };

  const queryResults = async (values: any, page: number) => {
    if (isInteractionDisabled) {
      setTableData({ isLoading: false, data: { hits: { hits: [], total: { value: 0 } } } });
      setAggData({ isLoading: false, data: {} });
      return;
    }
    let fuzzyValue = '';
    if (entityToggle) {
      fuzzyValue = 'AUTO';
    } else {
      fuzzyValue = '0';
    }
    const res = await DatahubAPI.trade({
      agg: true,
      indexName: selectedIndex,
      pageNum: page,
      primary: values.searchValue,
      listName: values.listName,
      hsCodeSection: values.hsCodeSection,
      hsCodeChapter: values.hsCodeChapter,
      hsCode: values.hsCode,
      sourceCountry: values.sourceCountry,
      agency: values.agency,
      state: values.state,
      fuzziness: fuzzyValue,
    });
    if (res?.success) {
      setAggData({
        isLoading: false,
        data: res?.result?.result?.body?.aggregations,
      });
      setTableData({ isLoading: false, data: res?.result?.result?.body });
    } else {
      enqueueSnackbar(
        'Error retrieving data. Commodities and Products data may be being updated',
        { variant: 'error', autoHideDuration: 5000 },
      );
    }
  };

  useEffect(() => {
    if (isInteractionDisabled) {
      return;
    }
    setFormValues({
      searchValue: '',
      hsCodeSection: [],
      hsCodeChapter: [],
      hsCode: [],
      sourceCountry: [],
      agency: [],
      state: [],
      listName: [],
      fuzziness: 0,
    });
    getInitialResults();
  }, [selectedIndex, isInteractionDisabled]);

  // dynamic table data filtering on dropdown option select/deselect
  useEffect(() => {
    if (isInteractionDisabled) {
      return;
    }
    queryResults(formValues, pageNum);
  }, [
    formValues.hsCodeSection,
    formValues.hsCodeChapter,
    formValues.hsCode,
    formValues.sourceCountry,
    formValues.agency,
    formValues.state,
    formValues.listName,
    isInteractionDisabled,
  ]);

  const helpContent = (
    <p className='module-header-tooltip-text'>
      The Commodities and Products module serves as a database where you can
      search commodities related to various global controls. Use the search bar
      to look up specific queries (HS Codes or commodities). Use Advanced
      Filters to select specific Listing Countries and List Names.
    </p>
  );

  return (
    <ModuleHeaderLayout
      title='Commodities and Products'
      helpContent={helpContent}
      hubLabel='Data Catalog'
      hubTo='/data/sets'
    >
      <div className='os-iframe'>
        <RequiredLicense userLicense={normalizedLicense} minimumLicense='pro' />
        <div className='bl-index-select'>
          <div
            className='bl-index-item'
            onClick={() => {
              if (!isInteractionDisabled) {
                handleIndexChange('HS Codes');
              }
            }}
            style={{ cursor: isInteractionDisabled ? 'not-allowed' : 'pointer' }}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    display: 'flex',
                    padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    borderRadius: 'var(--radius-md, 8px)',
                    background: 'var(--Background-200, var(--bg-colors))',
                    color: 'var(--text-prim25)',
                    maxWidth: '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, var(--bg-colors))',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  This dashboard is a simple lookup for HS Codes. Use the search
                  bar to look up specific queries (HS Codes or commodities). Use
                  Advanced Filters to select specific Listing Countries and List
                  Names.
                </p>
              }
            >
              <div className='bl-item-title'>HS Codes / Schedule B</div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'HS Codes' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
          <div
            className='bl-index-item'
            onClick={() => {
              if (!isInteractionDisabled) {
                handleIndexChange('Regimes');
              }
            }}
            style={{ cursor: isInteractionDisabled ? 'not-allowed' : 'pointer' }}
          >
            <ModuleContentTooltip
              placement='top'
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    display: 'flex',
                    padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    borderRadius: 'var(--radius-md, 8px)',
                    background: 'var(--Background-200, var(--bg-colors))',
                    color: 'var(--text-prim25)',
                    maxWidth: '400px',
                    '& .MuiTooltip-arrow': {
                      color: 'var(--Background-200, var(--bg-colors))',
                    },
                  },
                },
              }}
              title={
                <p className='module-header-tooltip-text'>
                  This dashboard includes control lists related to multiple
                  regimes, controls, and conventions. Use the search bar to look
                  up specific queries (HS Codes or commodities). Use Advanced
                  Filters to select specific Listing Countries and List Names.
                </p>
              }
            >
              <div className='bl-item-title'>
                Regimes / Controls / Conventions
              </div>
            </ModuleContentTooltip>
            <div className='bl-item-svg'>
              {selectedIndex === 'Regimes' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='206'
                  height='4'
                  viewBox='0 0 206 4'
                  fill='none'
                >
                  <path
                    d='M2 2L204.333 1.99998'
                    stroke='url(#paint0_linear_2173_3230)'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_2173_3230'
                      x1='2'
                      y1='2'
                      x2='204.333'
                      y2='1.99977'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#4CC9F0' />
                      <stop offset='0.453125' stopColor='#4895EF' />
                      <stop offset='1' stopColor='#4361EE' />
                    </linearGradient>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='207'
                  height='4'
                  viewBox='0 0 207 4'
                  fill='none'
                >
                  <path
                    d='M2.33337 2L204.667 2.00002'
                    stroke='#4A4C51'
                    strokeWidth='3'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </div>
          </div>
        </div>
        <TradeForm
          queryResults={queryResults}
          pageNum={pageNum}
          selectedIndex={selectedIndex}
          formValues={formValues}
          setFormValues={setFormValues}
          tradePrimaryOpts={tradePrimaryOpts}
          setTradePrimaryOpts={setTradePrimaryOpts}
          aggData={aggData}
          codeOpts={codeOpts}
          setCodeOpts={setCodeOpts}
          chapterOpts={chapterOpts}
          setChapterOpts={setChapterOpts}
          listOpts={listOpts}
          setListOpts={setListOpts}
          sourceCountryOpts={sourceCountryOpts}
          setSourceCountryOpts={setSourceCountryOpts}
          agencyOpts={agencyOpts}
          setAgencyOpts={setAgencyOpts}
          getInitialResults={getInitialResults}
          isInteractionDisabled={isInteractionDisabled}
        />
        <EntityResolution
          entityToggle={entityToggle}
          setEntityToggle={setEntityToggle}
          isInteractionDisabled={isInteractionDisabled}
        />
        {tableData.isLoading ? (
          <div>
            <CircularProgress style={{ color: '#0E8BFF' }} />
          </div>
        ) : (
          <HSCodesHitBreakdown
            toggleModal={toggleModal}
            selectedIndex={selectedIndex}
            queryResults={queryResults}
            formValues={formValues}
            pageNum={pageNum}
            setPageNum={setPageNum}
            tableData={tableData}
            isInteractionDisabled={isInteractionDisabled}
          />
        )}
        <Modal
          isOpen={fileNameModalOpen}
          onRequestClose={handleFileNameClose}
          ariaHideApp={false}
        >
          <div className='bite-score-file-modal'>
            <div className='display-flex justify-between width-100'>
              <Typography variant='h5'>File Name:</Typography>
              <Close onClick={handleFileNameClose} className='cursor-pointer' />
            </div>
            <div className='display-flex justify-end width-100'>
              <StyledInput
                value={fileName}
                onChange={handleFileNameChange}
                variant='outlined'
                className='margin-right-20px'
              />
              <NextButton
                variant='contained'
                color='primary'
                disabled={isInteractionDisabled}
                onClick={() => {
                  handleCSV();
                  handleFileNameClose();
                }}
              >
                Download
              </NextButton>
            </div>
          </div>
        </Modal>
      </div>
    </ModuleHeaderLayout>
  );
};

export default TradeProtectionReact;
